import { useState, type FormEvent } from "react";
import { useCreateFantasyFam } from "../lib/api/fantasy-fams";
import { useEarningsWeeks } from "../lib/api/market";
import { resolveBankroll } from "../lib/bankroll";
import { cleanInviteList } from "../lib/fantasy-fam-invites";
import type {
	BankrollTier,
	CreateFantasyFamResponse,
	FantasyFamCadence,
} from "../lib/types";
import { BankrollPicker } from "./BankrollPicker";

// Format an earnings week's Monday into a compact "May 25" label (UTC — the
// date string is a calendar date, not an instant).
function formatWeekLabel(startDate: string): string {
	return new Date(`${startDate}T00:00:00Z`).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		timeZone: "UTC",
	});
}

// A Fantasy Fam holds 5 members; the captain is one, so at most 4 invites
// can be sent at create time.
const MAX_INVITE_ROWS = 4;

interface CreateFantasyFamModalProps {
	// Whether the caller already has an ACTIVE Fantasy Fam membership. Free
	// users with one active are blocked; we surface a clear upgrade hint.
	hasActive: boolean;
	isPaid: boolean;
	onClose: () => void;
	onCreated: (response: CreateFantasyFamResponse) => void;
}

const CADENCES: readonly {
	value: FantasyFamCadence;
	label: string;
	blurb: string;
}[] = [
	{
		value: "weekly",
		label: "Weekly",
		blurb: "Mon → Fri composite. Settles every Friday close.",
	},
	{
		value: "quarterly",
		label: "Quarterly",
		blurb: "90-day lock vs SPY. Settles end of the calendar quarter.",
	},
	{
		value: "earnings",
		label: "Earnings",
		blurb: "Themed week where reporters score 1.5×.",
	},
];

export function CreateFantasyFamModal({
	hasActive,
	isPaid,
	onClose,
	onCreated,
}: CreateFantasyFamModalProps) {
	const [name, setName] = useState("");
	const [cadence, setCadence] = useState<FantasyFamCadence>("weekly");
	// Bankroll — free accounts are pinned to the $10K preset.
	const [bankrollTier, setBankrollTier] = useState<BankrollTier>("10k");
	const [customBankroll, setCustomBankroll] = useState("");
	// Earnings cadence only — the ISO week the Fam competes in. "" means
	// "use the first upcoming week" until the user picks otherwise.
	const [periodKey, setPeriodKey] = useState("");
	const earningsWeeks = useEarningsWeeks();
	// Optional invite rows — X handle / email / username. Stable ids so
	// removing a row doesn't reshuffle the controlled inputs.
	const [inviteRows, setInviteRows] = useState<{ id: string; value: string }[]>(() => [
		{ id: crypto.randomUUID(), value: "" },
	]);
	const [error, setError] = useState<string | null>(null);

	const create = useCreateFantasyFam();
	const submitting = create.isPending;
	const blocked = hasActive && !isPaid;
	const earningsWeekList = earningsWeeks.data?.weeks ?? [];

	function updateInvite(id: string, value: string) {
		setInviteRows((rows) => rows.map((r) => (r.id === id ? { ...r, value } : r)));
	}
	function addInvite() {
		setInviteRows((rows) =>
			rows.length >= MAX_INVITE_ROWS
				? rows
				: [...rows, { id: crypto.randomUUID(), value: "" }],
		);
	}
	function removeInvite(id: string) {
		setInviteRows((rows) =>
			rows.length === 1 ? rows : rows.filter((r) => r.id !== id),
		);
	}

	async function onSubmit(e: FormEvent) {
		e.preventDefault();
		setError(null);
		const cleaned = name.trim();
		if (!cleaned) {
			setError("Give your Fantasy Fam a name.");
			return;
		}
		if (cleaned.length > 50) {
			setError("Name must be 50 characters or fewer.");
			return;
		}
		const bankroll = resolveBankroll(bankrollTier, customBankroll);
		if (!bankroll.ok) {
			setError(bankroll.error);
			return;
		}
		const invites = cleanInviteList(inviteRows.map((r) => r.value));
		// Earnings cadence requires an ISO week; default to the first
		// upcoming one if the user didn't change the picker.
		let earningsPeriodKey: string | undefined;
		if (cadence === "earnings") {
			const weeks = earningsWeeks.data?.weeks ?? [];
			earningsPeriodKey = periodKey || weeks[0]?.week;
			if (!earningsPeriodKey) {
				setError(
					"No earnings week is available to pick yet — try again shortly.",
				);
				return;
			}
		}
		try {
			const result = await create.mutateAsync({
				name: cleaned,
				cadence,
				bankrollTier: bankroll.bankrollTier,
				...(bankroll.bankrollUsd !== undefined
					? { bankrollUsd: bankroll.bankrollUsd }
					: {}),
				...(invites.length > 0 ? { invites } : {}),
				...(earningsPeriodKey ? { periodKey: earningsPeriodKey } : {}),
			});
			onCreated(result);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Couldn't create Fantasy Fam.");
		}
	}

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label="Create a Fantasy Fam"
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
			onClick={(e) => {
				if (e.target === e.currentTarget && !submitting) onClose();
			}}
		>
			<div className="w-full max-w-md space-y-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5">
				<div className="flex items-start justify-between gap-3">
					<div>
						<h2 className="text-lg font-semibold">Create a Fantasy Fam</h2>
						<p className="text-xs text-[var(--color-muted)]">
							Pick a name + a cadence. Your Fantasy Fam lives for the
							duration of that competition period.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						disabled={submitting}
						className="rounded-md border border-[var(--color-border)] px-2 py-1 text-xs hover:border-[var(--color-accent)] disabled:opacity-50"
					>
						Close
					</button>
				</div>

				{blocked && (
					<div className="rounded-md border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 p-3 text-xs text-[var(--color-danger)]">
						Free tier — you already have an active Fantasy Fam. Finish the
						current cadence, or upgrade to compete across all three at once.
					</div>
				)}

				<form onSubmit={onSubmit} className="space-y-4">
					<label className="block space-y-1">
						<span className="text-sm font-medium">Fantasy Fam name</span>
						<input
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="e.g., Apes With Picks"
							maxLength={50}
							required
							autoFocus
							disabled={submitting || blocked}
							className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-3 py-2 text-sm placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none"
						/>
					</label>

					<fieldset className="space-y-2">
						<legend className="text-sm font-medium">Cadence</legend>
						<div className="space-y-2">
							{CADENCES.map((c) => (
								<label
									key={c.value}
									className={`flex cursor-pointer flex-col gap-1 rounded-md border p-3 text-left text-sm transition ${
										cadence === c.value
											? "border-[var(--color-accent)] bg-[var(--color-bg-elev)]"
											: "border-[var(--color-border)] hover:border-[var(--color-accent)]"
									}`}
								>
									<div className="flex items-center gap-2">
										<input
											type="radio"
											name="cadence"
											value={c.value}
											checked={cadence === c.value}
											onChange={() => setCadence(c.value)}
											disabled={submitting || blocked}
											className="h-4 w-4 accent-[var(--color-accent)]"
										/>
										<span className="font-semibold">{c.label}</span>
									</div>
									<span className="ml-6 text-xs text-[var(--color-muted)]">
										{c.blurb}
									</span>
								</label>
							))}
						</div>
						{!isPaid && (
							<p className="text-xs text-[var(--color-muted)]">
								Free tier — 1 active Fantasy Fam at a time. Upgrade to
								compete in all three cadences simultaneously.
							</p>
						)}
					</fieldset>

					{cadence === "earnings" && (
						<label className="block space-y-1">
							<span className="text-sm font-medium">Earnings week</span>
							{earningsWeeks.isLoading ? (
								<p className="text-xs text-[var(--color-muted)]">
									Loading earnings weeks…
								</p>
							) : earningsWeeks.isError ? (
								<p className="text-xs text-[var(--color-danger)]">
									Couldn't load earnings weeks — try again shortly.
								</p>
							) : earningsWeekList.length === 0 ? (
								<p className="text-xs text-[var(--color-muted)]">
									No upcoming earnings weeks available right now.
								</p>
							) : (
								<select
									value={periodKey || (earningsWeekList[0]?.week ?? "")}
									onChange={(e) => setPeriodKey(e.target.value)}
									disabled={submitting || blocked}
									className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none disabled:opacity-50"
								>
									{earningsWeekList.map((w) => (
										<option key={w.week} value={w.week}>
											Week of {formatWeekLabel(w.startDate)} —{" "}
											{w.count} reporters
										</option>
									))}
								</select>
							)}
							<span className="text-xs text-[var(--color-muted)]">
								Tickers reporting that week score 1.5×.
							</span>
						</label>
					)}

					<BankrollPicker
						tier={bankrollTier}
						customUsd={customBankroll}
						onTierChange={setBankrollTier}
						onCustomChange={setCustomBankroll}
						isPaid={isPaid}
						disabled={submitting || blocked}
					/>

					<fieldset className="space-y-2">
						<legend className="text-sm font-medium">
							Invite teammates (optional)
						</legend>
						<div className="space-y-2">
							{inviteRows.map((row) => (
								<div key={row.id} className="flex gap-2">
									<input
										type="text"
										value={row.value}
										onChange={(e) =>
											updateInvite(row.id, e.target.value)
										}
										placeholder="@x_handle, email, or username"
										disabled={submitting || blocked}
										className="flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-3 py-2 text-sm placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none disabled:opacity-50"
									/>
									{inviteRows.length > 1 && (
										<button
											type="button"
											onClick={() => removeInvite(row.id)}
											disabled={submitting || blocked}
											aria-label="Remove invite"
											className="rounded-md border border-[var(--color-border)] px-2 py-1 text-xs hover:border-[var(--color-danger)] hover:text-[var(--color-danger)] disabled:opacity-50"
										>
											×
										</button>
									)}
								</div>
							))}
						</div>
						{inviteRows.length < MAX_INVITE_ROWS && (
							<button
								type="button"
								onClick={addInvite}
								disabled={submitting || blocked}
								className="rounded-md border border-dashed border-[var(--color-border)] px-3 py-1.5 text-xs hover:border-[var(--color-accent)] disabled:opacity-50"
							>
								+ Add another
							</button>
						)}
						<p className="text-xs text-[var(--color-muted)]">
							Invite by X handle, email, or username — up to{" "}
							{MAX_INVITE_ROWS}. You can also share an invite link from the
							Fantasy Fam page after creating.
						</p>
					</fieldset>

					{error && (
						<p className="text-sm text-[var(--color-danger)]">{error}</p>
					)}

					<div className="flex justify-end gap-2">
						<button
							type="button"
							onClick={onClose}
							disabled={submitting}
							className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-50"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={submitting || blocked || name.trim().length === 0}
							className="rounded-md bg-[var(--color-accent)] px-4 py-1.5 text-sm font-semibold text-black hover:opacity-90 disabled:opacity-50"
						>
							{submitting ? "Creating…" : "Create Fantasy Fam"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
